import { styled } from "baseui";
import { DataTable, StatefulDataTable, StatefulDataTableProps } from "baseui/data-table";

import { StatefulMenu } from "baseui/menu";
import { Pagination, PaginationProps } from "baseui/pagination";
import { StatefulPopover } from "baseui/popover";
import { Flex } from "./Flex";
import { TableSkeleton } from "./TableSkeleton";

// Type assertions to handle BaseUI compatibility issues
const StatefulMenuComponent = StatefulMenu as any;
const PaginationComponent = Pagination as any;

const defaultGridConfig: Partial<StatefulDataTableProps> = {
  loadingMessage: "Loading...",
  emptyMessage: "No data available",
  resizableColumnWidths: true,
};

type GridActionProps<T> = {
  value: T;
  onAction: (value: T, action: string) => void;
  actions?: Array<{ label: string; id?: string }>;
};

// Custom 3 horizontal dots icon
const ThreeDotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="2" cy="8" r="1.5" fill="currentColor"/>
    <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
    <circle cx="14" cy="8" r="1.5" fill="currentColor"/>
  </svg>
);

const StyledDotsIcon = styled('div', () => ({
  cursor: "pointer",
  width: "24px",
  height: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "4px",
  color: "#6B7280",
  ":hover": {
    backgroundColor: "#F3F4F6",
    color: "#374151",
  },
}));

// Styled pagination container para eliminar espacio
const PaginationWrapper = styled('div', {
  marginTop: '0',
  paddingTop: '10px',
  borderTop: '1px solid #F0F0F0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingLeft: '16px',
  paddingRight: '16px',
});

const PaginationInfo = styled('div', {
  fontSize: '14px',
  color: '#6B7280',
  fontWeight: '500',
});

const PaginationControls = styled('div', {
  display: 'flex',
  alignItems: 'center',
});

export const GridActions = <T extends object>({
  value,
  onAction,
  actions = [
    { label: "Resend invitation" },
    { label: "View details" },
  ],
}: GridActionProps<T>) => (
  <StatefulPopover
    content={({ close }) => (
      <StatefulMenuComponent
        items={actions}
        onItemSelect={({ item }: { item: any }) => {
          onAction(value, item.label);
          close();
        }}
      />
    )}
    placement="bottom"
  >
    <StyledDotsIcon>
      <ThreeDotsIcon />
    </StyledDotsIcon>
  </StatefulPopover>
);

type BaseGridProps = StatefulDataTableProps & {
  pagination?: PaginationProps & {
    totalRecords?: number;
    currentLimit?: number;
  };
  loading?: boolean;
  rows?: Array<any>;
  columns?: Array<any>;
  onRowDoubleClick?: (rowData: any) => void;
  sortable?: boolean;
  onColumnSort?: (columnTitle: string) => void;
};

export const BaseGrid = ({ pagination, loading, rows, columns, onRowDoubleClick, onColumnSort, ...otherProps }: BaseGridProps) => {
  const dataTableProps: any = {
    ...defaultGridConfig,
    rows: rows || [],
    columns: columns || [],
    ...otherProps,
  };

  // Add column sort handler if provided
  if (onColumnSort) {
    dataTableProps.onColumnSort = ({ columnTitle }: { columnTitle: string }) => {
      onColumnSort(columnTitle);
    };
  }

  // Add double-click functionality if provided
  if (onRowDoubleClick) {
    dataTableProps.overrides = {
      ...dataTableProps.overrides,
      Row: {
        component: ({ children, ...props }: any) => (
          <div
            {...props}
            onDoubleClick={() => {
              if (props.row && props.row.data) {
                onRowDoubleClick(props.row.data);
              }
            }}
            style={{
              ...props.style,
              cursor: 'pointer',
            }}
          >
            {children}
          </div>
        ),
      },
    };
  }

  return (
    <Flex height="100%" flexDirection="column" width={"100%"}>
      {loading ? (
        <TableSkeleton rows={4} />
      ) : (
        <>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <StatefulDataTable {...dataTableProps} />
            </div>
          {pagination && pagination.numPages > 0 ? (
            <PaginationWrapper>
              <PaginationInfo>
                {pagination.totalRecords && pagination.currentLimit ? (
                  <>
                    Showing {Math.min((pagination.currentPage - 1) * pagination.currentLimit + 1, pagination.totalRecords)} to{' '}
                    {Math.min(pagination.currentPage * pagination.currentLimit, pagination.totalRecords)} of{' '}
                    {pagination.totalRecords} results
                  </>
                ) : (
                  `Page ${pagination.currentPage} of ${pagination.numPages}`
                )}
              </PaginationInfo>
              <PaginationControls>
                <PaginationComponent
                  numPages={pagination.numPages}
                  size="mini"
                  currentPage={pagination.currentPage}
                  onPageChange={pagination.onPageChange}
                />
              </PaginationControls>
            </PaginationWrapper>
          ) : null}
        </>
      )}
    </Flex>
  );
};
